import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import HeroSection from '@/components/HeroSection';
import CategoryFilter from '@/components/CategoryFilter';
import SolutionGrid from '@/components/SolutionGrid';
import Button from '@/components/Button';
import { ChevronRight, PlusCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import AddSolutionDialog from '@/components/AddSolutionDialog';
import { useSolutionFiltering } from '@/hooks/useSolutionFiltering';
import { AdSense } from '@/components/ui/adsense';
import NewAndTrendingSection from '@/components/NewAndTrendingSection';
import BeAmazedSection from '@/components/BeAmazedSection';
import { GoogleAdsToggleService } from '@/services/googleAdsToggleService';
import DynamicMetaTags from '@/components/DynamicMetaTags';

const Index = () => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [showAllSolutions, setShowAllSolutions] = useState(false);
  const [showAds, setShowAds] = useState(false);
  const [adsLoading, setAdsLoading] = useState(true);
  
  const {
    categories,
    filteredSolutions,
    selectedCategory,
    isLoading,
    isFiltering,
    isError,
    filterError,
    setSelectedCategory,
    refetch,
    getCategoryName
  } = useSolutionFiltering('home-solutions-categories');

  // Google Ads toggle logic
  useEffect(() => {
    const checkGoogleAdsToggle = async () => {
      try {
        const shouldShow = await GoogleAdsToggleService.shouldDisplayGoogleAds();
        setShowAds(shouldShow);
      } catch (error) {
        console.error('Error checking Google Ads toggle:', error);
        // Use safe default on error
        setShowAds(false);
      } finally {
        setAdsLoading(false);
      }
    };

    checkGoogleAdsToggle();
  }, []);

  const showContent = !isError || filteredSolutions.length > 0;
  
  // Filter to show only featured solutions unless showAllSolutions is true or a category is selected
  const displayedSolutions = showAllSolutions || selectedCategory 
    ? filteredSolutions 
    : filteredSolutions.filter(solution => solution.featured);

  return (
    <Layout>
      <DynamicMetaTags
        title="AIZen.tips - Find the best AI Solutions for your needs"
        description="AIzen.tips is an AI discovery platform, a collaborative encyclopedia where users can browse, evaluate, discover and get recommendations of AI solutions, all through visual and auditory demonstrations before making any external commitments."
        canonical="https://aizen.tips/"
        ogImage="https://nwweejpidselzqtwvgnb.supabase.co/storage/v1/object/public/aizen-pub-bucket/AIZen-welcome.png"
      />
      
      <HeroSection />

      <BeAmazedSection />

      {/* New and Trending Section */}
      <NewAndTrendingSection />
      
      {/* Google Ads - now controlled by toggle */}
      {!adsLoading && (
        <div className="container mx-auto px-4 mt-8">
          <AdSense 
            className="mb-8" 
            variant="compact" 
            shouldDisplay={showAds}
          />
        </div>
      )}

      <section className="py-8 md:py-10">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 gap-4">
            <div className="flex-1">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-medium tracking-tight mb-2 sm:mb-3 text-left">
                Explore AI Solutions
              </h2>
              <p className="text-muted-foreground max-w-2xl text-left text-sm sm:text-base">
                Browse our comprehensive collection of AI solutions across various categories.
                Filter by category or search to find exactly what you're looking for.
              </p>
            </div>
            <Button 
              onClick={() => setIsAddDialogOpen(true)}
              className="flex items-center gap-2"
            >
              <PlusCircle size={16} />
              Add Solution
            </Button>
          </div>

          {showContent && (
            <>
              <CategoryFilter
                categories={categories}
                selectedCategory={selectedCategory}
                onSelectCategory={(category) => {
                  setSelectedCategory(category);
                  setShowAllSolutions(true);
                }}
                isLoading={isLoading}
                isFiltering={isFiltering}
                className="mb-8"
              />

              <SolutionGrid 
                solutions={displayedSolutions}
                isLoading={isLoading || isFiltering}
                selectedCategory={selectedCategory}
                categoryName={getCategoryName(selectedCategory)}
                className="mb-12"
              />
            </>
          )}

          {/* Graceful degradation for error states - no explicit error UI */}
          {isError && filteredSolutions.length === 0 && (
            <div className="text-center py-16">
              <div className="mx-auto w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
                <PlusCircle className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-medium mb-2">Welcome to AI Solutions</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Discover and share cutting-edge AI tools and technologies. 
                Be the first to add a solution to our platform.
              </p>
              <Button 
                onClick={() => setIsAddDialogOpen(true)}
                className="flex items-center gap-2 mx-auto"
              >
                <PlusCircle className="w-4 h-4" />
                Add First Solution
              </Button>
            </div>
          )}

          <div className="text-center">
            {!showAllSolutions && !selectedCategory ? (
              <Button 
                variant="outline" 
                className="flex items-center gap-2 mx-auto"
                onClick={() => setShowAllSolutions(true)}
              >
                View All Solutions
                <ChevronRight size={16} />
              </Button>
            ) : (
              <Link to="/explore">
                <Button variant="outline" className="flex items-center gap-2 mx-auto">
                  Explore More
                  <ChevronRight size={16} />
                </Button>
              </Link>
            )}
          </div>
        </div>
      </section>

      <AddSolutionDialog 
        open={isAddDialogOpen} 
        onOpenChange={(open) => {
          setIsAddDialogOpen(open);
          // Refetch data when dialog closes after potential success
          if (!open) {
            refetch();
          }
        }}
      />
    </Layout>
  );
};

export default Index;
